import { buildQueryString } from "~/utils/url";
import { appointmentsKeys } from "../../../appointments/api/queries";
import { appointmentsQueryFilterOptions, AppointmentsResponse } from "../../../appointments/api/types";
import { apiClient } from "~/lib/client";
import { handleApiError, handleResponse } from "~/utils/response";
import { queryOptions } from "@tanstack/react-query";
import { CalendarEvent, RefillEvent } from "../../components/types";
import { addHours } from "date-fns";
import { RefillsResponse } from "../../../encounters/api/types";
import { refillsKeys } from "../../../refills/api/queries/keys";

export const calendarAppointmentsQueryOptions = (opts: appointmentsQueryFilterOptions) => {
    return queryOptions({
        queryKey: appointmentsKeys.calendarAppointments(opts),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/appointments${buildQueryString(opts)}`);
                const result = await handleResponse<AppointmentsResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        select(data) {
            return data?.appointments?.map(appointment => {
                const event = {
                  id: appointment.appointment_id,
                  type: 'appointment',
                  title: appointment.title,
                  subtitle: appointment.subtitle, // new field, may be undefined
                  description: appointment.description,
                  start: appointment.appointment_datetime,
                  end: addHours(appointment.appointment_datetime, appointment.time_interval_minutes ? appointment.time_interval_minutes / 60 : 1),
                  location: appointment.location,
                  clientName: appointment.patient?.name,
                  email: appointment.patient?.email,
                  status: appointment.status === 'attended' ? 'completed' : 
                          appointment.status === 'cancelled' ? 'cancelled' : 'scheduled',
                  zoom_metadata: {
                      created_at: appointment.zoom_metadata?.created_at,
                      start_url: appointment.zoom_metadata?.start_url,
                      join_url: appointment.zoom_metadata?.join_url
                  },
                  session: appointment.session,
                  encounter: appointment.encounter
                } satisfies CalendarEvent;
                return event;
            }) || [];
        }
    });
};

export const calendarConsultationQueryOptions = (opts: appointmentsQueryFilterOptions) => {
    return queryOptions({
        queryKey: appointmentsKeys.calendarAppointments(opts),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/consultations${buildQueryString(opts)}`);
                const result = await handleResponse<AppointmentsResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        select(data) {
            return data?.appointments?.map(appointment => {
                const event = {
                  id: appointment.appointment_id,
                  type: 'appointment',
                  title: appointment.title,
                  description: appointment.description,
                  start: appointment.appointment_datetime,
                  end: addHours(appointment.appointment_datetime, 1),
                  location: appointment.location,
                  clientName: appointment.patient.name,
                  email: appointment.patient.email,
                  status: appointment.status === 'attended' ? 'completed' : 
                          appointment.status === 'cancelled' ? 'cancelled' : 'scheduled',
                  zoom_metadata: {
                      created_at: appointment.zoom_metadata?.created_at,
                      start_url: appointment.zoom_metadata?.start_url,
                      join_url: appointment.zoom_metadata?.join_url
                  }
                } satisfies CalendarEvent;
                return event;
            }) || [];
        }
    });
};

export const calendarRefillsQueryOptions = (opts: appointmentsQueryFilterOptions) => {
    return queryOptions({
        queryKey: refillsKeys.refills(opts),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/refills${buildQueryString(opts)}`);
                const result = await handleResponse<RefillsResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        select(data) {
            return data?.refills?.map(refill => {
                const startDate = new Date(refill.request_datetime);
                const event = {
                  id: refill.refill_id,
                  type: 'refill',
                  title: `Refill Request - ${refill.medication.name}`,
                  description: `Refill request for ${refill.medication.name}`,
                  start: startDate,
                  end: addHours(startDate, 1),
                  prescriptionId: refill.prescription_id,
                  clientName: refill.patient.name,
                  medication: refill.medication.name,
                  quantity: 0,
                  request_datetime: refill.request_datetime,
                  status: refill.status === 'approved' ? 'accepted' :
                          refill.status === 'requested' ? 'pending' : 'rejected'
                } satisfies RefillEvent;
                return event;
            }) || [];
        }
    });
};